"use client";

import React, { useMemo, useState } from "react";
import { ResponsiveModal, ModalContainer, MODAL_IDS, Calendar } from "@wtc/ui";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useUpdateDeal } from "@/features/deals/hooks/use-deals-hooks";
import {
  addSnoozeTagsForRange,
  removeAllSnoozeTags,
  getSnoozedWeeks,
} from "@/lib/utils/task/snooze-tagging";
import { getWeekStringsInRange } from "@/lib/utils/task/iso-week";
import type { SnoozeDealModalData } from "@/lib/modals/data/modal-data-types";
import type { DateRange } from "react-day-picker";
import { useTodayUtcMidnight } from "@/lib/hooks/use-today-utc-midnight";

// Mounted via the metadata-driven modal registry (lib/modals/metadata/deals.ts).
// Self-contained: it receives dealId + current tags through the modal-stack data
// payload and writes the merged tag array via the codegen-typed useUpdateDeal
// hook — it does NOT read DetailActionContext (which is null at the global mount).
// See .claude/rules/GLOBAL_MODAL_NO_ACTION_CONTEXT.md.

export function SnoozeDealModal() {
  return (
    <ModalContainer<SnoozeDealModalData> modalId={MODAL_IDS.deals.snooze}>
      {({ isOpen, data, onClose }) => (
        <SnoozeDealModalContent isOpen={isOpen} data={data} onClose={onClose} />
      )}
    </ModalContainer>
  );
}

interface SnoozeDealModalContentProps {
  isOpen: boolean;
  data?: SnoozeDealModalData;
  onClose: () => void;
}

function SnoozeDealModalContent({ isOpen, data, onClose }: SnoozeDealModalContentProps) {
  const updateDeal = useUpdateDeal();
  const [range, setRange] = useState<DateRange | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);

  const currentTags = data?.tags ?? [];
  const dealId = data?.dealId;

  const activeSnoozedWeeks = useMemo(() => getSnoozedWeeks(currentTags), [currentTags]);

  // Weeks the selected range would snooze — drives the live preview.
  const previewWeeks = useMemo(() => {
    if (!range?.from) return [];
    return getWeekStringsInRange(range.from, range.to ?? range.from);
  }, [range]);

  const todayUtcMidnight = useTodayUtcMidnight();

  if (!dealId) return null;

  const handleSnooze = async () => {
    if (!range?.from) {
      toast.error("Pick a date range to snooze");
      return;
    }
    setIsSaving(true);
    try {
      const updatedTags = addSnoozeTagsForRange(currentTags, range.from, range.to ?? range.from);
      await updateDeal.mutateAsync({ id: dealId, tags: updatedTags });
      toast.success(
        previewWeeks.length === 1
          ? `Follow-up reminders snoozed for ${formatWeek(previewWeeks[0])}`
          : `Follow-up reminders snoozed for ${previewWeeks.length} weeks`
      );
      onClose();
    } catch (error) {
      console.error("Error snoozing deal:", error);
      toast.error("Failed to snooze deal");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClearSnooze = async () => {
    setIsSaving(true);
    try {
      const updatedTags = removeAllSnoozeTags(currentTags);
      await updateDeal.mutateAsync({ id: dealId, tags: updatedTags });
      toast.success("Snooze cleared — follow-up reminders resumed");
      onClose();
    } catch (error) {
      console.error("Error clearing deal snooze:", error);
      toast.error("Failed to clear snooze");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ResponsiveModal
      globalModalId={MODAL_IDS.deals.snooze}
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Snooze follow-up reminders"
      description={
        data?.dealTitle
          ? `Pick the weeks you don't want "${data.dealTitle}" follow-up reminders.`
          : "Pick the weeks you don't want follow-up reminders for this deal."
      }
      maxWidth="md"
    >
      <div className="p-4 space-y-4">
        {activeSnoozedWeeks.length > 0 && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-3">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Currently snoozed: {activeSnoozedWeeks.map(formatWeek).join(", ")}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleClearSnooze}
              disabled={isSaving}
            >
              Clear snooze
            </Button>
          </div>
        )}

        <div className="flex justify-center">
          {/* `todayUtcMidnight` resolves on the client (see useTodayUtcMidnight).
              Rendering the picker before then would anchor it to a placeholder. */}
          {todayUtcMidnight && (
          <Calendar
            mode="range"
            selected={range}
            onSelect={setRange}
            disabled={{ before: todayUtcMidnight }}
            defaultMonth={todayUtcMidnight}
            numberOfMonths={2}
          />
          )}
        </div>

        <div className="rounded-lg border bg-card-50 p-3 min-h-[3rem]">
          {previewWeeks.length > 0 ? (
            <p className="text-sm text-card-foreground">
              This snoozes{" "}
              <span className="font-medium">
                {previewWeeks.length === 1
                  ? formatWeek(previewWeeks[0])
                  : `${previewWeeks.length} weeks (${formatWeek(previewWeeks[0])} – ${formatWeek(
                      previewWeeks[previewWeeks.length - 1]
                    )})`}
              </span>
              . You won't get follow-up reminders for those weeks.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Select a start and end date above to preview the snoozed weeks.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSnooze} disabled={isSaving || !range?.from}>
            {isSaving ? "Snoozing…" : "Snooze"}
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
}

/** "2026-W24" → "Week 24, 2026" for human-readable display. */
function formatWeek(week: string): string {
  const match = /^(\d{4})-W(\d{2})$/.exec(week);
  if (!match) return week;
  return `Week ${Number(match[2])}, ${match[1]}`;
}
